/**
 * The two grading phases whose unit is NOT (task × submission) — pulled out of `runGradingAgent` so each can be
 * a job of its own.
 *
 * WHY THEY HAD TO COME OUT. A grading run used to be one main task across a whole cohort, and that shape was
 * chosen by its LAST phase rather than its first: grading a student's answer is one model call per (task,
 * submission), but reconciling those grades compares students against each other, so the run had to hold a cohort
 * to have anything to compare. That single fact is what forced a submission SET into the job's identity — and a
 * set is the one thing a dedupe key cannot express, which is where the late scope resolution, the dedupe window
 * ending at the claim, and the per-wave store of asks all came from.
 *
 * So the unit moves to the actual unit and the aggregates become their own rows:
 *
 *  - `grading`             — one (main task × submission). Explicit, dedupable, independently retryable.
 *  - `gradingConsistency`  — one MAIN TASK, once its grading rows are done. This file's review half.
 *  - `languageUseGroupGrading` — one (submission × language-use scheme GROUP), because a group spans several main tasks
 *                            and would otherwise be graded once per member task.
 *
 * All three are the `grading` STEP (see `EXTRA_STEP_TYPES`), so the wave is not over until all of them are and
 * the announcement still fires once.
 *
 * REFERENCES ARE THE OTHER HALF of what this file adds. A wave that regrades three submissions can only make
 * those three consistent with each other, which pulls them away from the cohort they sit in — actively worse than
 * not reviewing at all. So the review is given the rest of the cohort's existing grades as read-only
 * REFERENCES: they establish the standard, the wave's own grades are brought into line with it, and nothing
 * outside the wave is ever written. That is also what makes the review possible for a single submission, which
 * today is silently exempt (`comparableGroups` needs two entries and a lone regrade only ever has one).
 */

import { logger } from '@adornis/base/logging.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import type { LanguageUseSchemeLike } from '../../../shared/grammar-rules-resolution.js';
import { Exam, type ExamTask } from '../../exam.js';
import { Submission, type SubmissionTask } from '../../submission.js';
import { ownMaxPoints } from '../../utils/points-model.js';
import { upsertLanguageUseRuleResult } from '../language-use-rule-result.js';
import {
  gradeLanguageUseSchemeGroup,
  reviewGradingConsistencyForTask,
  type ConsistencyReviewInput,
  type LanguageUseSchemeGroupMember,
} from './default-grading.js';
import {
  collectNodesByTaskNumber,
  gradingUnitId,
  languageUseUnitRef,
  markGradingUnitFinished,
  openGradingProgressRun,
} from './gradingAgent.js';

/**
 * How many other students' grades a review is shown. Twenty rather than the cohort because the reviewer's job is
 * to read the ESTABLISHED STANDARD off them, and twenty spread across the score range say what four hundred say —
 * while a four-hundred-entry prompt is why per-entry excerpts had to be capped in the first place.
 */
const REFERENCE_SAMPLE_SIZE = 20;

/* ------------------------------------------------------------------ *
 * Phase 2 — consistency, per main task
 * ------------------------------------------------------------------ */

/** Anything → the string the reviewer can be shown, so the builders below need no per-field fallbacks. */
const asText = (value: unknown): string => (typeof value === 'string' ? value : '');

/** Anything → an array, for walking a tree whose shape is only known at runtime. */
const asNodes = (value: unknown): any[] => (Array.isArray(value) ? value : []);

/**
 * One graded node, in the shape the reviewer reads — as READ BACK from the submission, not as produced by a
 * grading pass. Null when the node was never graded.
 *
 * NO WRITE COORDINATES, deliberately, and that is what makes this cheap. A grading pass derives
 * `gradingResultFieldPath` and its `arrayFilters` as it builds its update; rebuilding them here would mean
 * reproducing that logic in a second place, where the two could drift. A reference is never written, so it does
 * not need them — and `reviewGradingConsistencyForTask` refuses to adjust an entry that has none, which turns the
 * missing field into the enforcement rather than a hazard.
 *
 * A node with no score says nothing about the standard, so it contributes SILENCE rather than a zero — a zero
 * would drag the reviewer's sense of the range down with it.
 */
function referenceItemForNode(
  submissionId: string,
  examNode: any,
  submissionNode: any,
  nodeLabel: string,
): ConsistencyReviewInput | null {
  if (!examNode || !submissionNode) return null;
  const dbId = asText(examNode._id);
  const result = submissionNode.gradingResult;
  const score = Number(result?.score);
  if (!dbId || !Number.isFinite(score)) return null;

  return {
    submissionId,
    dbId,
    nodeLabel,
    criteria: asText(examNode.gradingScheme?.criteria),
    score,
    maxPoints: ownMaxPoints(examNode),
    explanation: asText(result.explanation),
    studentAnswerExcerpt: asText(submissionNode.studentAnswer),
    role: 'reference',
  };
}

/**
 * One submission's whole subtree under a main task.
 *
 * Walks the exam node and the submission node TOGETHER, because the two ids come from different sides: `dbId`
 * groups entries across students and belongs to the EXAM node, while the score and the answer are the
 * SUBMISSION's. Paired by `taskNumber` within one parent, which is where that label is unique.
 */
function referenceItemsForSubmission(
  submissionId: string,
  examNode: any,
  submissionNode: any,
  nodeLabel: string,
): ConsistencyReviewInput[] {
  const own = referenceItemForNode(submissionId, examNode, submissionNode, nodeLabel);
  const out: ConsistencyReviewInput[] = own ? [own] : [];

  const submissionChildren = asNodes(submissionNode?.subTasksSubmission);
  for (const examChild of asNodes(examNode?.subTasks)) {
    const childNumber = asText(examChild?.taskNumber);
    if (!childNumber) continue;
    const submissionChild = submissionChildren.find(node => asText(node?.taskNumber) === childNumber);
    if (!submissionChild) continue;
    out.push(...referenceItemsForSubmission(submissionId, examChild, submissionChild, childNumber));
  }

  return out;
}

/** Every submission that has a score for this main task and is not one of the wave's own, with that score. */
async function referenceCandidates(
  examId: string,
  taskNumber: string,
  excluded: Set<string>,
): Promise<Array<{ submissionId: string; node: any; score: number }>> {
  // `$elemMatch` IN THE PROJECTION, so the server returns only this main task's element of `taskSubmissions`. The
  // whole array is the student's entire graded exam, and this reads the cohort once per task of the wave — the
  // difference between a few hundred kilobytes and a few hundred megabytes. The subtree under the matched element
  // still comes back, which is what the reference walk needs.
  const submissionCollection = await getRawCollection(Submission._collectionName);
  const rows = await submissionCollection
    .find(
      { examId, taskSubmissions: { $elemMatch: { taskNumber } } },
      { projection: { _id: 1, taskSubmissions: { $elemMatch: { taskNumber } } } },
    )
    .toArray();

  const candidates: Array<{ submissionId: string; node: any; score: number }> = [];
  for (const row of rows) {
    const submissionId = String((row as any)._id);
    if (excluded.has(submissionId)) continue;
    const node = asNodes((row as any).taskSubmissions)[0];
    const score = Number(node?.gradingResult?.score);
    if (!node || !Number.isFinite(score)) continue;
    candidates.push({ submissionId, node, score });
  }
  return candidates;
}

/**
 * A spread of the cohort's existing grades for one main task, excluding the submissions this wave graded.
 *
 * SPREAD ACROSS THE SCORE RANGE rather than the first twenty found, because the point is to show the reviewer
 * what a 0, a 3 and a full-marks answer look like in this exam. Twenty consecutive students from one class list
 * can easily all sit in the same band and would teach it nothing about the edges. Sorted by the main task's own
 * score and sampled at even stride — the cheapest thing that actually covers the range.
 */
export async function buildConsistencyReferences(
  examId: string,
  taskNumber: string,
  excludeSubmissionIds: readonly string[],
): Promise<ConsistencyReviewInput[]> {
  const examCollection = await getCollection(Exam as any);
  const exam = await examCollection.findOne({ _id: examId } as any, { projection: { tasks: 1 } });
  const examNode = asNodes((exam as any)?.tasks).find(task => asText(task?.taskNumber) === taskNumber);
  if (!examNode) return [];

  const candidates = await referenceCandidates(examId, taskNumber, new Set(excludeSubmissionIds.map(String)));
  if (candidates.length === 0) return [];

  candidates.sort((a, b) => a.score - b.score);
  const stride = Math.max(1, Math.ceil(candidates.length / REFERENCE_SAMPLE_SIZE));
  const sampled = candidates.filter((_, index) => index % stride === 0).slice(0, REFERENCE_SAMPLE_SIZE);

  return sampled.flatMap(candidate =>
    referenceItemsForSubmission(candidate.submissionId, examNode, candidate.node, taskNumber),
  );
}

/**
 * Reviews one main task's grades and writes the adjustments. The block that used to sit inside
 * `runGradingAgent`'s per-task loop, unchanged in what it does.
 *
 * NON-FATAL BY CONTRACT, as it was inline: the first-pass grades are already written and correct on their own, so
 * a failed reconciliation must not fail the row that carries it and lose them. It is reported and the wave
 * continues.
 */
export async function runTaskConsistencyReview(
  examId: string,
  taskNumber: string,
  items: ConsistencyReviewInput[],
): Promise<{ adjustedCount: number; rubricNote?: string }> {
  if (items.length === 0) return { adjustedCount: 0 };

  const { adjustments, rubricNote } = await reviewGradingConsistencyForTask(items, { examId });
  const rawSubmissions = await getRawCollection(Submission._collectionName);

  for (const adjustment of adjustments) {
    // Sequential deliberately: these are positional writes into per-student documents and there are at most a
    // handful, so a fan-out buys nothing and costs the ability to stop cleanly on a signal.
    // eslint-disable-next-line no-await-in-loop -- one small write per adjustment; see above
    await rawSubmissions.updateOne(
      { _id: adjustment.submissionId } as any,
      {
        $set: {
          [`${adjustment.gradingResultFieldPath}.score`]: adjustment.score,
          [`${adjustment.gradingResultFieldPath}.actualPointsGiven`]: adjustment.score,
          [`${adjustment.gradingResultFieldPath}.explanation`]: adjustment.explanation,
          [`${adjustment.gradingResultFieldPath}.gradingSource`]: 'ai_consistency_adjusted',
          // Reset so the teacher knows this node needs a fresh look. An approved grade is deliberately not
          // exempt from being re-checked — but only ever as a TARGET, which means only in a wave that graded it.
          [`${adjustment.gradingResultFieldPath}.isApproved`]: false,
        },
      },
      { arrayFilters: adjustment.arrayFilters },
    );
  }

  if (adjustments.length > 0) {
    logger.info(
      { examId, taskNumber, adjustedCount: adjustments.length },
      'Consistency-review pass adjusted scores for task',
    );
  }
  if (rubricNote) {
    logger.warn({ examId, taskNumber, rubricNote }, 'Consistency-review pass flagged a possible rubric gap');
  }

  return { adjustedCount: adjustments.length, ...(rubricNote ? { rubricNote } : {}) };
}

/* ------------------------------------------------------------------ *
 * Phase 3 — language-use scheme groups, per (submission × group)
 * ------------------------------------------------------------------ */

/**
 * Grades one language-use scheme group for one submission — the thunk `runGradingAgent` used to build inline, as a
 * function a job can call.
 *
 * The group's members are resolved HERE rather than passed in, because a member is a (exam task, submission task)
 * pair and neither survives a payload: the tree can be rebuilt between the enqueue and the run. The row carries
 * the two ids that are stable — the submission and the scheme — and looks the rest up.
 *
 * OWNS ITS PROGRESS UNIT AT BOTH ENDS. It opens the progress run and claims the unit here, and marks it finished
 * on every path, including failure, for the same reason the per-task grading does (see `markGradingUnitFinished`):
 * the unit is no longer outstanding, and leaving it out stalls the bar on a state the teacher cannot act on.
 *
 * The claim used to be made by the content run that enqueued this row, which only worked while the two were
 * strictly ordered. They are not any more — this row is dispatched up front alongside the content rows and can
 * be claimed before any of them. A content run starting after this row finished would pull the unit back out of
 * the graded set with nothing left to put it back, and `markGradingUnitFinished` would refuse to write at all if
 * no content run had opened the record yet. Opening it here is what makes this row independent of both.
 */
export async function runLanguageUseGroupForSubmission(
  examId: string,
  submissionId: string,
  schemeId: string,
): Promise<{ graded: boolean; note: string }> {
  const unitId = gradingUnitId(submissionId, languageUseUnitRef(schemeId));
  try {
    // Read once and handed to both: `openGradingProgressRun` needs this same projection to size the exam's unit
    // total, and would otherwise fetch the document a second time immediately after we do. Inside the `try` so
    // that a failed read still reaches the `finally` and releases the unit.
    const examCollection = await getCollection(Exam as any);
    const exam = await examCollection.findOne({ _id: examId } as any, {
      projection: { tasks: 1, languageUseSchemeRules: 1 },
    });
    const examTasks: any[] = Array.isArray((exam as any)?.tasks) ? (exam as any).tasks : [];
    const rules: LanguageUseSchemeLike[] = Array.isArray((exam as any)?.languageUseSchemeRules)
      ? (exam as any).languageUseSchemeRules
      : [];

    await openGradingProgressRun(examId, [unitId], { examTasks, languageUseSchemeRules: rules });

    const scheme = rules.find(rule => asText((rule as { _id?: unknown })._id) === schemeId);
    if (!scheme) return { graded: false, note: 'language-use scheme is gone' };

    const submissionCollection = await getRawCollection(Submission._collectionName);
    const submission = await submissionCollection.findOne({ _id: submissionId } as any, {
      projection: { taskSubmissions: 1 },
    });
    if (!submission) return { graded: false, note: 'submission is gone' };

    // Keyed by the same qualified path on both sides, which is what `collectNodesByTaskNumber` builds — a bare
    // taskNumber is not unique across the tree, so matching on it alone can pair a subtask with a namesake
    // somewhere else entirely.
    const examByNumber = collectNodesByTaskNumber((exam as any)?.tasks as any[], 'subTasks');
    const submissionByNumber = collectNodesByTaskNumber(
      (submission as any).taskSubmissions as any[],
      'subTasksSubmission',
    );

    const members: LanguageUseSchemeGroupMember[] = ((scheme as { dependsOn?: unknown[] }).dependsOn ?? [])
      .map(memberTaskNumber => {
        const examTask = examByNumber.get(String(memberTaskNumber));
        const submissionTask = submissionByNumber.get(String(memberTaskNumber));
        return examTask && submissionTask
          ? { examTask: examTask as unknown as ExamTask, submissionTask: submissionTask as unknown as SubmissionTask }
          : null;
      })
      .filter((member): member is LanguageUseSchemeGroupMember => member !== null);

    if (members.length === 0) return { graded: false, note: 'no member task of this group is present' };

    const result = await gradeLanguageUseSchemeGroup(
      scheme as unknown as { _id: string; criteria: string; maxPoints: number },
      members,
      { examId },
    );
    await upsertLanguageUseRuleResult(submissionId, schemeId, result);
    return { graded: true, note: `language-use group ${schemeId} graded` };
  } finally {
    await markGradingUnitFinished(examId, unitId);
  }
}
